import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { CartService } from '@core/services/cart/cart.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { BasketService } from '@core/services/basket/basket.service';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { interval, Observable, Subscription, throwError, timer } from 'rxjs';
import { DoodOrderModel } from '@store/order/order.model';
import { OrderKeys } from '@config/keys/order.keys';
import { catchError, delay, map, takeUntil, tap } from 'rxjs/operators';
import { PaymentMethodHandles } from '@config/payment-methods.config';
import { DoodApiError } from '@shared/interfaces/error.interface';
import { Paths } from '@config/paths.config';
import { NeptingTerminalService } from '@core/services/nepting-terminal/nepting-terminal.service';
import {
  NeptingCaptureResponse,
  NeptingPreauthResponse,
  NeptingTerminalApiService,
} from '@core/services/api/nepting-terminal/nepting-terminal-api.service';
import { KioskService } from '@core/services/kiosk/kiosk.service';
import { NeptingMerchantContract } from '@core/models/kiosk.model';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Component({
  selector: 'app-payment-panel-nepting-terminal',
  templateUrl: './payment-panel-nepting-terminal.component.html',
  styles: [],
  standalone: false,
})
export class PaymentPanelNeptingTerminalComponent
  extends DestroyerBase
  implements OnInit, OnDestroy
{
  readonly terminalInstructionMessage = input<string>();

  statusMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  isPaid = false;
  isOnError = false;
  isLoading = true;
  loadKioskResetTimer = false;
  paidOrder?: DoodOrderModel;
  showReceiptChoice = false;
  receiptPrintCountdown = 20;
  isPrintingReceipt = false;

  private readonly router = inject(Router);
  private readonly cartService = inject(CartService);
  private readonly ordersService = inject(OrdersService);
  private readonly basketService = inject(BasketService);
  private readonly paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly neptingTerminalService = inject(NeptingTerminalService);
  private readonly neptingTerminalApiService = inject(NeptingTerminalApiService);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly kioskService = inject(KioskService);
  private readonly analyticsService = inject(AnalyticsService);

  private countdownSub?: Subscription;
  private receiptLines: string[] = [];
  private merchantContract: string = NeptingMerchantContract.ERT10;

  ngOnInit(): void {
    this.neptingTerminalApiService
      .merchantContract$()
      .pipe(takeUntil(this._destroyerRef))
      .subscribe({
        next: data => {
          this.merchantContract = data.merchant_contract;
          this.createOrder();
        },
        error: () => {
          this.errorMessage = 'Erreur de configuration du terminal.';
          this.isOnError = true;
          this.isLoading = false;
        },
      });

    this.neptingTerminalApiService.startDisplayStream();

    this.neptingTerminalApiService.status$
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(message => {
        this.statusMessage = message;
      });

    timer(1).subscribe(() => {
      this.paymentDispatcher.updateUI({
        error: null,
        button: {
          isEnabled: false,
          isVisible: false,
        },
        status: {
          isLoading: false,
        },
      });
    });
  }

  async ngOnDestroy(): Promise<void> {
    this.neptingTerminalApiService.stopDisplayStream();
    this.countdownSub?.unsubscribe();

    if (!this.isPaid) {
      this.statusMessage = 'ANNULATION';
      await this.neptingTerminalService.abortTransaction$().pipe(delay(3000)).toPromise();
    }

    super.ngOnDestroy();
  }

  createOrder(): void {
    this.isOnError = false;
    this.isLoading = true;
    this.resetMessages();
    this.loadKioskResetTimer = false;

    this.loadingMessage = 'Validation de votre commande';

    this.ordersService
      .createOrder$(PaymentMethodHandles.NeptingTerminal)
      .pipe(
        takeUntil(this._destroyerRef),
        tap(() => (this.isLoading = false)),
        map(order => (order ? this.startTransaction(order) : null)),
      )
      .subscribe({
        error: (response: { error: DoodApiError }) => {
          this.handleOrderError(response.error);
        },
      });
  }

  private updateOrderStatusToPayment(order: DoodOrderModel): Observable<void> {
    return this.neptingTerminalService
      .changeOrderStatusToPayment$(order.id, order[OrderKeys.Number] ?? 'UNKNOWN')
      .pipe(map(() => void 0));
  }

  private startTransaction(order: DoodOrderModel): void {
    const finalPrice = order[OrderKeys.FinalPrice];
    this.resetMessages();
    this.loadingMessage = 'Connexion au terminal de paiement';

    if (typeof finalPrice !== 'number') {
      this.errorMessage = 'Error: invalid amount';
      return;
    }

    const contract = this.merchantContract;

    if (contract === NeptingMerchantContract.ERT10) {
      // Flow ERT10 : simple transaction
      this.neptingTerminalApiService
        .transaction$(finalPrice)
        .pipe(
          tap(response => {
            if (!response.success) {
              this.handlePaymentError(response);
              return;
            }

            this.updateOrderStatusToPayment(order)
              .pipe(
                tap(() => {
                  const receipt = response.holder_receipt ?? [];
                  this.handlePaymentSuccess(order, { success: true, holder_receipt: receipt });
                }),
                catchError(err => {
                  this.handlePaymentError({
                    success: false,
                    holder_receipt: response.holder_receipt,
                  });
                  return throwError(() => err);
                }),
              )
              .subscribe();
          }),
          catchError(err => {
            this.handlePaymentError({ success: false });
            return throwError(() => err);
          }),
        )
        .subscribe();
    } else {
      // Flow ERT45 : preauth + capture
      this.neptingTerminalApiService
        .preAuthorize$(finalPrice)
        .pipe(
          tap(preauth => this.handlePreAuthorization(order, preauth, finalPrice)),
          catchError(err => {
            this.handlePaymentError({ success: false });
            return throwError(() => err);
          }),
        )
        .subscribe();
    }
  }

  private handlePreAuthorization(
    order: DoodOrderModel,
    preauth: NeptingPreauthResponse,
    finalPrice: number,
  ): void {
    this.isLoading = false;

    if (!preauth.success) {
      this.handlePaymentError(preauth);
      return;
    }

    const preauthTicket = preauth.holder_receipt ?? [];

    this.updateOrderStatusToPayment(order)
      .pipe(
        tap(() =>
          this.capturePayment(
            order,
            preauth.authorization_id!,
            finalPrice,
            preauth.token!,
            preauthTicket,
          ),
        ),
        catchError(err => {
          this.cancelPreAuthorization(preauth.authorization_id!, preauth.token!, preauthTicket);
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  private capturePayment(
    order: DoodOrderModel,
    authorizationId: string,
    finalPrice: number,
    token: string,
    fallbackReceipt: string[],
  ): void {
    this.neptingTerminalApiService
      .capture$(authorizationId, finalPrice, token)
      .pipe(
        tap((capture: NeptingCaptureResponse) => {
          const receipt = capture.success ? (capture.holder_receipt ?? []) : fallbackReceipt;
          this.handlePaymentSuccess(order, { success: true, holder_receipt: receipt });
        }),
        catchError(err => {
          this.handlePaymentSuccess(order, { success: true, holder_receipt: fallbackReceipt });
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  private cancelPreAuthorization(
    authorizationId: string,
    token: string,
    fallbackReceipt: string[],
  ): void {
    this.neptingTerminalApiService
      .capture$(authorizationId, 0, token)
      .pipe(
        tap((capture: NeptingCaptureResponse) => {
          const receipt = capture.holder_receipt ?? fallbackReceipt;
          this.handlePaymentError({ success: false, holder_receipt: receipt });
        }),
        catchError(err => {
          this.handlePaymentError({ success: false, holder_receipt: fallbackReceipt });
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  private handlePaymentError(response: { success: boolean; holder_receipt?: string[] }): void {
    this.loadKioskResetTimer = true;
    this.isLoading = false;
    this.errorMessage = 'Transaction annulée';
    this.statusMessage = 'ERREUR';
    this.isOnError = true;

    if (response.holder_receipt) {
      this.kioskService.printLines(response.holder_receipt);
    }
  }

  private handlePaymentSuccess(
    order: DoodOrderModel,
    response: {
      success: boolean;
      holder_receipt?: string[];
    },
  ): void {
    this.isPaid = true;
    this.receiptLines = response.holder_receipt ?? [];
    this.showReceiptChoice = true;
    this.paidOrder = order;

    this.startReceiptCountdown();
  }

  private startReceiptCountdown(): void {
    this.countdownSub = interval(1000)
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(() => {
        this.receiptPrintCountdown--;
        if (this.receiptPrintCountdown <= 0) {
          this.printReceiptAndContinue();
        }
      });
  }

  printReceiptAndContinue(): void {
    this.countdownSub?.unsubscribe();
    this.isPrintingReceipt = true;
    this.kioskService.printLines(this.receiptLines);
    this.redirectAfterReceipt();
  }

  skipReceiptAndContinue(): void {
    this.countdownSub?.unsubscribe();
    this.redirectAfterReceipt();
  }

  private redirectAfterReceipt(): void {
    this.basketService.clearBasket();
    this.cartService.clearCart();
    this.router.navigate([
      this.routerHelper.translateRoute(`${Paths.Orders}/${this.paidOrder?.id}/${Paths.Status}`),
    ]);
  }

  private handleOrderError(error: Partial<DoodApiError>): void {
    this.isLoading = false;
    this.errorMessage = error.detail ?? 'Une erreur est survenue.';
  }

  private resetMessages(): void {
    this.loadingMessage = '';
    this.errorMessage = '';
    this.statusMessage = '';
  }

  backToCart(): void {
    this.router.navigate([this.routerHelper.translateRoute(`${Paths.Cart}`)]);
  }
}
